'use client'

import { Loader } from '@fukku/loader/Loader'
import { cx } from 'classix'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { type ReactNode, useEffect, useState } from 'react'
import { BUTTON_VARIANT } from '../Button.types'

import styles from './ButtonContentWrapper.module.css'

type ButtonContentWrapperProps = {
	readonly children: ReactNode
	readonly variant: BUTTON_VARIANT
	readonly isLoading?: boolean
	readonly accessibilityText?: { loaded: string; loading: string }
}

const LOADER_DELAY = 300

export const ButtonContentWrapper = (props: ButtonContentWrapperProps) => {
	const [showLoading, setShowLoading] = useState(false)
	const { t } = useLabels()

	const {
		children,
		isLoading,
		variant,
		accessibilityText = {
			loading: t('accessibility.loadingPage.text'),
			loaded: t('accessibility.uploadedPage.text'),
		},
	} = props

	useEffect(() => {
		if (!isLoading) {
			setShowLoading(false)
			return () => {}
		}

		const timer = setTimeout(() => {
			setShowLoading(true)
		}, LOADER_DELAY)

		return () => clearTimeout(timer)
	}, [isLoading])

	const showInverseLoading = variant === BUTTON_VARIANT.DEFAULT

	const handleAnimationEnd = () => {
		setShowLoading(true)
	}

	return isLoading && showLoading ? (
		<Loader
			accessibilityText={accessibilityText}
			inverse={showInverseLoading}
			loading
			className={styles.loader}
		/>
	) : (
		<span
			className={cx(styles.content, isLoading && styles.loadingText)}
			onAnimationEnd={handleAnimationEnd}
		>
			{children}
		</span>
	)
}
